import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission, PAGES, PAGE_INFO } from '../contexts/PermissionContext';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    userProfile,
    user,
    logout,
    loading: authLoading
  } = useAuth();

  const {
    canAccessPage,
    hasWildcardAccess,
    getVisibleTags,
    loading: permLoading
  } = usePermission();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const loading = authLoading || permLoading;

  // 點擊外部關閉選單
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    if (path === '/projects') {
      return location.pathname.startsWith('/projects') ? 'bg-purple-400' : '';
    }
    return location.pathname === path ? 'bg-purple-400' : '';
  };

  // 處理登出
  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    await logout();
    navigate('/login');
  };

  // 如果還在載入中，顯示簡化版 Navbar
  if (loading) {
    return (
      <nav className="bg-purple-300 text-white shadow-lg sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <span className="text-xl font-bold">ESG 問卷管理系統</span>
          </div>
        </div>
      </nav>
    );
  }

  // 如果未登入，顯示簡化版 Navbar
  if (!isAuthenticated) {
    return (
      <nav className="bg-purple-300 text-white shadow-lg sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/login" className="text-xl font-bold">
              ESG 問卷管理系統
            </Link>
            <Link
              to="/login"
              className="px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition font-medium"
            >
              登入
            </Link>
          </div>
        </div>
      </nav>
    );
  }

  // 取得權限標籤顯示
  const visibleTags = getVisibleTags();
  const isAdmin = hasWildcardAccess();

  return (
    <nav className="bg-purple-300 text-white shadow-lg sticky top-0 z-40">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="text-xl font-bold">
            ESG 問卷管理系統
          </Link>

          <div className="flex items-center space-x-4">
            {/* 首頁 - 所有登入用戶都可見 */}
            <Link
              to="/"
              className={`px-3 py-2 rounded-md hover:bg-purple-400 transition flex items-center gap-1 ${isActive('/')}`}
            >
              🏠 首頁
            </Link>

            {/* 專案管理 */}
            {(isAdmin || canAccessPage(PAGES.PROJECTS)) && (
              <Link
                to="/projects"
                className={`px-3 py-2 rounded-md hover:bg-purple-400 transition flex items-center gap-1 ${isActive('/projects')}`}
              >
                {PAGE_INFO[PAGES.PROJECTS].icon} {PAGE_INFO[PAGES.PROJECTS].label}
              </Link>
            )}

            {/* 年度供應商資料（原報表庫） */}
            {(isAdmin || canAccessPage(PAGES.REPORTS)) && (
              <Link
                to="/reports"
                className={`px-3 py-2 rounded-md hover:bg-purple-400 transition flex items-center gap-1 ${isActive('/reports')}`}
              >
                {PAGE_INFO[PAGES.REPORTS].icon} {PAGE_INFO[PAGES.REPORTS].label}
              </Link>
            )}

            {/* 設定 */}
            {(isAdmin || canAccessPage(PAGES.SETTINGS)) && (
              <Link
                to="/settings"
                className={`px-3 py-2 rounded-md hover:bg-purple-400 transition flex items-center gap-1 ${isActive('/settings')}`}
              >
                {PAGE_INFO[PAGES.SETTINGS].icon} {PAGE_INFO[PAGES.SETTINGS].label}
              </Link>
            )}

            {/* 使用者選單 */}
            <div className="relative ml-4" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-400 hover:bg-purple-500 transition"
              >
                <div className="h-8 w-8 rounded-full bg-white text-purple-600 flex items-center justify-center font-medium">
                  {(userProfile?.display_name || user?.email || '?')[0].toUpperCase()}
                </div>
                <span className="hidden md:block text-sm max-w-[120px] truncate">
                  {userProfile?.display_name || user?.email?.split('@')[0]}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* 下拉選單 */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg py-2 z-50">
                  {/* 使用者資訊 */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-medium">
                        {(userProfile?.display_name || user?.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {userProfile?.display_name || '未設定名稱'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {user?.email}
                        </div>
                      </div>
                    </div>
                    {/* 管理員標籤 */}
                    {isAdmin && (
                      <div className="mt-2">
                        <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-yellow-500 text-white">
                          管理員
                        </span>
                      </div>
                    )}
                    {/* 權限標籤 */}
                    {!isAdmin && visibleTags.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-500 mb-1">資料存取範圍：</div>
                        <div className="flex flex-wrap gap-1">
                          {visibleTags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="inline-block px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700"
                            >
                              {tag}
                            </span>
                          ))}
                          {visibleTags.length > 3 && (
                            <span className="inline-block px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-500">
                              +{visibleTags.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 選單項目 */}
                  <div className="py-1">
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      登出
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
